<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TeletelAuditEventsAPI from 'dashboard/api/teletelAuditEvents';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import KpiCard from 'dashboard/components-next/teletel/KpiCard.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';

/**
 * Teletel audit trail, read-only.
 *
 * Not related to the Chatwoot Enterprise "Audit Logs" page: separate route,
 * separate component, separate API. Nothing here can create, edit or delete an
 * event - the server refuses those operations regardless.
 */
const { t } = useI18n();

// Fallback until the first page arrives; the server's full list replaces it.
const FALLBACK_ACTIONS = [
  'create',
  'update',
  'destroy',
  'conversation.status_changed',
  'conversation.assigned',
  'conversation.priority_changed',
  'contact.created',
  'contact.updated',
  'contact.deleted',
  'campaign.created',
  'campaign.status_changed',
  'campaign.retry_initiated',
  'campaign.recipient_status_changed',
  'inbox.created',
  'inbox.updated',
  'team.membership_changed',
];

const events = ref([]);
const ACTIONS = ref(FALLBACK_ACTIONS);
const meta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const summary = ref({
  total: 0,
  created: 0,
  updated: 0,
  deleted: 0,
  teletel: 0,
});
const isLoading = ref(false);
const errorMessage = ref('');

const filters = reactive({
  action_name: '',
  actor_id: '',
  from: '',
  to: '',
  page: 1,
});

const hasEvents = computed(() => events.value.length > 0);
const isEmpty = computed(() => !isLoading.value && !hasEvents.value);
const canGoPrevious = computed(() => meta.value.current_page > 1);
const canGoNext = computed(
  () => meta.value.current_page < meta.value.total_pages
);

const requestParams = () => {
  const params = { page: filters.page };
  if (filters.action_name) params.action_name = filters.action_name;
  if (filters.actor_id) params.actor_id = filters.actor_id;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  return params;
};

const fetchEvents = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await TeletelAuditEventsAPI.list(requestParams());
    events.value = data.payload;
    meta.value = data.meta;
    if (Array.isArray(data.meta?.actions) && data.meta.actions.length)
      ACTIONS.value = data.meta.actions;
    summary.value = data.summary;
  } catch (error) {
    events.value = [];
    summary.value = {
      total: 0,
      created: 0,
      updated: 0,
      deleted: 0,
      teletel: 0,
    };
    errorMessage.value = t('TELETEL_AUDIT_TRAIL.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  filters.page = 1;
  fetchEvents();
};

const resetFilters = () => {
  filters.action_name = '';
  filters.actor_id = '';
  filters.from = '';
  filters.to = '';
  applyFilters();
};

const goToPage = page => {
  filters.page = page;
  fetchEvents();
};

const formatTime = value => (value ? new Date(value).toLocaleString() : '—');

const actionLabel = action =>
  t(
    `TELETEL_AUDIT_TRAIL.ACTIONS.${action.toUpperCase().replace('.', '_')}`,
    action
  );

const actorLabel = actor => {
  if (!actor) return t('TELETEL_AUDIT_TRAIL.SYSTEM_ACTOR');
  return actor.name || `${actor.type} #${actor.id}`;
};

const targetLabel = event => {
  if (!event.auditable_type) return '—';
  return `${event.auditable_type} #${event.auditable_id}`;
};

const summaryCards = computed(() => [
  { key: 'total', value: summary.value.total },
  { key: 'created', value: summary.value.created },
  { key: 'updated', value: summary.value.updated },
  { key: 'deleted', value: summary.value.deleted },
  { key: 'teletel', value: summary.value.teletel },
]);

const changeEntries = event => Object.entries(event.audited_changes || {});

const formatChangeValue = value => {
  if (value === null || value === undefined || value === '') {
    return t('TELETEL_AUDIT_TRAIL.EMPTY_VALUE');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

onMounted(fetchEvents);
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_AUDIT_TRAIL.HEADER')"
      :description="t('TELETEL_AUDIT_TRAIL.DESCRIPTION')"
    />

    <section class="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <KpiCard
        v-for="card in summaryCards"
        :key="card.key"
        :label="t(`TELETEL_AUDIT_TRAIL.SUMMARY.${card.key.toUpperCase()}`)"
        :value="card.value"
        :tone="
          card.key === 'deleted'
            ? 'danger'
            : card.key === 'created'
              ? 'success'
              : card.key === 'teletel'
                ? 'brand'
                : 'neutral'
        "
        :icon="
          card.key === 'created'
            ? 'i-lucide-circle-plus'
            : card.key === 'deleted'
              ? 'i-lucide-trash-2'
              : card.key === 'teletel'
                ? 'i-lucide-shield-check'
                : 'i-lucide-activity'
        "
      />
    </section>

    <ContentCard>
      <form
        class="flex flex-wrap items-end gap-3"
        @submit.prevent="applyFilters"
      >
        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{
            t('TELETEL_AUDIT_TRAIL.FILTERS.FROM')
          }}</span>
          <input v-model="filters.from" type="date" class="h-9 !mb-0" />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{
            t('TELETEL_AUDIT_TRAIL.FILTERS.TO')
          }}</span>
          <input v-model="filters.to" type="date" class="h-9 !mb-0" />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{
            t('TELETEL_AUDIT_TRAIL.FILTERS.ACTION')
          }}</span>
          <select v-model="filters.action_name" class="h-9 !mb-0">
            <option value="">
              {{ t('TELETEL_AUDIT_TRAIL.FILTERS.ALL_ACTIONS') }}
            </option>
            <option v-for="action in ACTIONS" :key="action" :value="action">
              {{ actionLabel(action) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{
            t('TELETEL_AUDIT_TRAIL.FILTERS.ACTOR')
          }}</span>
          <input
            v-model="filters.actor_id"
            type="number"
            min="1"
            :placeholder="t('TELETEL_AUDIT_TRAIL.FILTERS.ACTOR_PLACEHOLDER')"
            class="h-9 !mb-0"
          />
        </label>

        <div class="flex gap-2">
          <button type="submit" class="button">
            {{ t('TELETEL_AUDIT_TRAIL.FILTERS.APPLY') }}
          </button>
          <button type="button" class="button clear" @click="resetFilters">
            {{ t('TELETEL_AUDIT_TRAIL.FILTERS.RESET') }}
          </button>
        </div>
      </form>
    </ContentCard>

    <p v-if="errorMessage" class="text-sm text-n-ruby-11">{{ errorMessage }}</p>

    <p v-if="isLoading" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('TELETEL_AUDIT_TRAIL.LOADING') }}
    </p>

    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center gap-2 py-16 text-center"
    >
      <p class="text-base font-medium text-n-slate-12">
        {{ t('TELETEL_AUDIT_TRAIL.EMPTY.TITLE') }}
      </p>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('TELETEL_AUDIT_TRAIL.EMPTY.SUBTITLE') }}
      </p>
    </div>

    <ContentCard v-else flush>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-start text-n-slate-11">
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.TIME') }}
              </th>
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.ACTOR') }}
              </th>
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.ACTION') }}
              </th>
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.TARGET') }}
              </th>
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.SOURCE') }}
              </th>
              <th class="p-2 font-medium text-start">
                {{ t('TELETEL_AUDIT_TRAIL.TABLE.CHANGES') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="event in events"
              :key="event.id"
              class="border-t border-n-weak"
            >
              <td class="p-2 whitespace-nowrap">
                {{ formatTime(event.created_at) }}
              </td>
              <td class="p-2">{{ actorLabel(event.actor) }}</td>
              <td class="p-2">{{ actionLabel(event.action) }}</td>
              <td class="p-2">{{ targetLabel(event) }}</td>
              <td class="p-2 text-n-slate-11">{{ event.ip_address || '—' }}</td>
              <td class="p-2 min-w-64">
                <details v-if="changeEntries(event).length">
                  <summary class="text-sm cursor-pointer text-n-blue-11">
                    {{ t('TELETEL_AUDIT_TRAIL.TABLE.SHOW_CHANGES') }}
                  </summary>
                  <dl class="grid gap-2 mt-2">
                    <div
                      v-for="[field, value] in changeEntries(event)"
                      :key="field"
                      class="p-2 rounded-lg bg-n-slate-2"
                    >
                      <dt class="text-xs font-medium text-n-slate-12">
                        {{ field }}
                      </dt>
                      <dd
                        v-if="Array.isArray(value)"
                        class="mt-1 text-xs text-n-slate-11"
                      >
                        {{
                          t('TELETEL_AUDIT_TRAIL.CHANGE_TRANSITION', {
                            before: formatChangeValue(value[0]),
                            after: formatChangeValue(value[1]),
                          })
                        }}
                      </dd>
                      <dd v-else class="mt-1 text-xs text-n-slate-11">
                        {{ formatChangeValue(value) }}
                      </dd>
                    </div>
                  </dl>
                </details>
                <span v-else class="text-n-slate-10">
                  {{ t('TELETEL_AUDIT_TRAIL.EMPTY_VALUE') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <nav v-if="hasEvents" class="flex items-center justify-between gap-3">
      <span class="text-sm text-n-slate-11">
        {{
          t('TELETEL_AUDIT_TRAIL.PAGINATION.SUMMARY', {
            page: meta.current_page,
            total: meta.total_pages,
            count: meta.total_count,
          })
        }}
      </span>
      <span class="flex gap-2">
        <button
          type="button"
          class="button clear"
          :disabled="!canGoPrevious"
          @click="goToPage(meta.current_page - 1)"
        >
          {{ t('TELETEL_AUDIT_TRAIL.PAGINATION.PREVIOUS') }}
        </button>
        <button
          type="button"
          class="button clear"
          :disabled="!canGoNext"
          @click="goToPage(meta.current_page + 1)"
        >
          {{ t('TELETEL_AUDIT_TRAIL.PAGINATION.NEXT') }}
        </button>
      </span>
    </nav>
  </div>
</template>
